// =============================================================================
// NAD+ Lifestyle Gallery (UGC) Section Component
// =============================================================================

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import GLightbox from 'glightbox';

const PREVIEW_LOOP_SECONDS = 5;

export class NADLifestyleGallery {
  private element: HTMLElement;
  private swiper: Swiper | null = null;
  private lightbox: ReturnType<typeof GLightbox> | null = null;
  private fadeObserver: IntersectionObserver | null = null;
  private videoObserver: IntersectionObserver | null = null;
  private soundHandlers: Array<{ button: HTMLElement; handler: (e: Event) => void }> = [];
  private expandHandlers: Array<{ button: HTMLElement; handler: (e: Event) => void }> = [];
  private previewLoopHandlers: Array<{ video: HTMLVideoElement; handler: () => void }> = [];
  private resizeHandler: (() => void) | null = null;
  private resizeTimeout: number | undefined;

  constructor(element: HTMLElement) {
    this.element = element;
    this.init();
  }

  private init(): void {
    this.initSwiper();
    this.setupScrollAnimation();
    this.setupVideoPlayback();
    this.setupSoundToggles();
    this.setupLightbox();
  }

  /**
   * The track now sits inside the 1680px __container (with the 120px section
   * gutter), so on desktop the slides align to the container edge with no
   * extra offset. Mobile keeps the design's 20px peek offset.
   */
  private getEdgeOffset(): number {
    return window.innerWidth < 1024 ? 20 : 0;
  }

  private initSwiper(): void {
    const swiperElement = this.element.querySelector<HTMLElement>('.nad-lifestyle-gallery__track');
    if (!swiperElement) return;

    this.swiper = new Swiper(swiperElement, {
      modules: [Navigation],
      slidesPerView: 'auto',
      spaceBetween: 12,
      slidesOffsetBefore: this.getEdgeOffset(),
      slidesOffsetAfter: this.getEdgeOffset(),
      grabCursor: true,
      centerInsufficientSlides: true,
      // Arrows below the slider (desktop-only via CSS); Swiper hides them
      // with .swiper-button-lock while all slides fit
      navigation: {
        prevEl: this.element.querySelector<HTMLElement>('.nad-lifestyle-gallery__nav-btn--prev'),
        nextEl: this.element.querySelector<HTMLElement>('.nad-lifestyle-gallery__nav-btn--next')
      }
    });

    this.resizeHandler = () => {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => {
        if (!this.swiper) return;
        const offset = this.getEdgeOffset();
        this.swiper.params.slidesOffsetBefore = offset;
        this.swiper.params.slidesOffsetAfter = offset;
        this.swiper.update();
      }, 250);
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Fade in each card as it becomes visible; stagger delays come from the
   * nth-child rules in the section SCSS
   */
  private setupScrollAnimation(): void {
    const items = this.element.querySelectorAll<HTMLElement>('.nad-lifestyle-gallery__item');

    this.fadeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            this.fadeObserver?.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -30px 0px'
      }
    );

    items.forEach((item) => this.fadeObserver?.observe(item));
  }

  /**
   * Preview videos: play only while on screen, looping the first 5 seconds
   */
  private setupVideoPlayback(): void {
    const videos = this.element.querySelectorAll<HTMLVideoElement>('.nad-lifestyle-gallery__video');
    if (videos.length === 0) return;

    videos.forEach((video) => {
      const handler = () => {
        if (video.currentTime >= PREVIEW_LOOP_SECONDS) {
          video.currentTime = 0;
        }
      };
      video.addEventListener('timeupdate', handler);
      this.previewLoopHandlers.push({ video, handler });
    });

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            video.play().catch(() => { /* autoplay may be blocked */ });
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.3 }
    );

    videos.forEach((video) => this.videoObserver?.observe(video));
  }

  private setupSoundToggles(): void {
    const buttons = this.element.querySelectorAll<HTMLElement>('.nad-lifestyle-gallery__sound');

    buttons.forEach((button) => {
      const handler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        const media = button.closest('.nad-lifestyle-gallery__media');
        const video = media?.querySelector<HTMLVideoElement>('.nad-lifestyle-gallery__video');
        if (!video) return;

        video.muted = !video.muted;
        button.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
      };

      button.addEventListener('click', handler);
      this.soundHandlers.push({ button, handler });
    });
  }

  /**
   * Open the full video/image in a GLightbox modal when a card is clicked
   */
  private setupLightbox(): void {
    const mediaElements = [...this.element.querySelectorAll<HTMLElement>('.nad-lifestyle-gallery__media[data-gl-src]')];
    if (mediaElements.length === 0) return;

    this.lightbox = GLightbox({
      elements: mediaElements.map((media) => ({
        href: media.dataset.glSrc as string,
        type: (media.dataset.glType as 'video' | 'image') || 'image'
      })),
      autoplayVideos: true,
      touchNavigation: true,
      loop: false
    });

    mediaElements.forEach((media, index) => {
      const button = media.querySelector<HTMLElement>('.nad-lifestyle-gallery__expand');
      if (!button) return;

      const handler = (e: Event) => {
        e.preventDefault();
        // Pause the preview while the modal is open
        media.querySelector<HTMLVideoElement>('.nad-lifestyle-gallery__video')?.pause();
        this.lightbox?.openAt(index);
      };

      button.addEventListener('click', handler);
      this.expandHandlers.push({ button, handler });
    });
  }

  public destroy(): void {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    window.clearTimeout(this.resizeTimeout);
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.lightbox?.destroy();
    this.lightbox = null;
    this.fadeObserver?.disconnect();
    this.fadeObserver = null;
    this.videoObserver?.disconnect();
    this.videoObserver = null;
    this.soundHandlers.forEach(({ button, handler }) => button.removeEventListener('click', handler));
    this.soundHandlers = [];
    this.expandHandlers.forEach(({ button, handler }) => button.removeEventListener('click', handler));
    this.expandHandlers = [];
    this.previewLoopHandlers.forEach(({ video, handler }) => video.removeEventListener('timeupdate', handler));
    this.previewLoopHandlers = [];
    this.element.querySelectorAll('.nad-lifestyle-gallery__item.animate-in')
      .forEach(el => el.classList.remove('animate-in'));
  }

  static initializeAll(): void {
    document.querySelectorAll<HTMLElement>('.nad-lifestyle-gallery')
      .forEach(el => {
        if (!(el as any)._nadComponentInstance) {
          (el as any)._nadComponentInstance = new NADLifestyleGallery(el);
        }
      });
  }
}
